<template>
  <div class="settings-page" v-loading="loading">
    <!-- 基本信息 -->
    <ProjectInfoCard :project-id="projectId" />

    <!-- 团队成员管理 -->
    <MemberSection
      v-if="showMemberSection"
      :project-id="projectId"
      :members="teamMembers"
      :excluded-user-ids="excludedTeamUserIds"
      :current-user="currentUser"
      :can-manage="canManage"
      @refresh="loadProjectMembers"
    />

    <!-- 客户管理 -->
    <ClientSection
      v-if="showClientSection"
      :project-id="projectId"
      :clients="clientMembers"
      @refresh="loadProjectMembers"
      @show-perm="openPermDialog"
    />

    <!-- 代理管理 -->
    <AgentSection
      v-if="showAgentSection"
      :project-id="projectId"
      :agents="agentMembers"
      @refresh="loadProjectMembers"
    />

    <!-- QC 管理 -->
    <QcSection
      v-if="showQcSection"
      :project-id="projectId"
      :qc-members="qcMembers"
      @refresh="loadProjectMembers"
    />

    <!-- 神客考试认证 -->
    <ExamSection v-if="showExamSection" :project-id="projectId" />

    <!-- 神客信息总览 -->
    <ShopperOverviewSection v-if="showShopperOverview" :project-id="projectId" />

    <!-- 危险操作 -->
    <DangerZone v-if="showDangerZone" :project-id="projectId" />

    <!-- 权限弹窗 -->
    <PermissionDialog
      v-if="showPermissionDialog"
      :visible.sync="permDialogVisible"
      :user="permUser"
      :user-role="permUserRole"
      :project-id="projectId"
      @saved="loadProjectMembers"
    />
  </div>
</template>

<script>
import { getProjectMembersByRole } from '@/app/index/api/project/manager'
import ProjectInfoCard from './components/settings/ProjectInfoCard.vue'
import MemberSection from './components/settings/MemberSection.vue'
import ClientSection from './components/settings/ClientSection.vue'
import AgentSection from './components/settings/AgentSection.vue'
import QcSection from './components/settings/QcSection.vue'
import ExamSection from './components/settings/ExamSection.vue'
import ShopperOverviewSection from './components/settings/ShopperOverviewSection.vue'
import DangerZone from './components/settings/DangerZone.vue'
import PermissionDialog from './components/settings/PermissionDialog.vue'

export default {
  name: 'ProjectSettings',
  components: { ProjectInfoCard, MemberSection, ClientSection, AgentSection, QcSection, ExamSection, ShopperOverviewSection, DangerZone, PermissionDialog },
  data() {
    return {
      loading: false,
      allMembersFlat: [],
      clientMembers: [],
      agentMembers: [],
      qcMembers: [],
      teamMembers: [],
      // 权限弹窗
      permDialogVisible: false,
      permUser: null,
      permUserRole: ''
    }
  },
  computed: {
    projectId() {
      return this.$route.params.projectId
    },
    currentUser() {
      return this.$store.getters.userInfo || {}
    },
    myRole() {
      const uid = this.currentUser.uid || this.currentUser.id
      if (!uid) return ''
      const me = this.allMembersFlat.find(m => (m.UserId || m.userId || m.uid) == uid)
      return me ? (me.Role || me.role || '') : ''
    },
    normalizedRole() {
      return (this.myRole || '').toLowerCase()
    },
    isRD() {
      return ['rd', 'admin'].includes(this.normalizedRole)
    },
    isQC() {
      return ['qc', 'reviewer', '质量管控'].includes(this.normalizedRole)
    },
    isOps() {
      return ['ops', '执行'].includes(this.normalizedRole)
    },
    canManage() {
      return this.isRD
    },
    showMemberSection() {
      return this.isRD
    },
    showClientSection() {
      return this.isRD
    },
    showAgentSection() {
      return this.isRD || this.isOps
    },
    showQcSection() {
      return this.isRD || this.isQC
    },
    showExamSection() {
      return this.isRD
    },
    showShopperOverview() {
      return this.isRD || this.isOps
    },
    showDangerZone() {
      return this.isRD
    },
    showPermissionDialog() {
      return this.isRD
    },
    excludedTeamUserIds() {
      const collectIds = (list) => (list || [])
        .map(m => m.UserId || m.userId || m.uid)
        .filter(v => v !== undefined && v !== null && v !== '')

      // 团队成员下拉仅排除代理/客户，QC 属于内部员工，保留可选
      return [
        ...collectIds(this.agentMembers),
        ...collectIds(this.clientMembers)
      ]
    }
  },
  mounted() {
    this.loadProjectMembers()
  },
  methods: {
    async loadProjectMembers() {
      this.loading = true
      try {
        const res = await getProjectMembersByRole({ ProjectId: parseInt(this.projectId) })
        const raw = res?.data?.data || res?.data || []

        if (Array.isArray(raw)) {
          this.allMembersFlat = raw
        } else {
          this.allMembersFlat = [
            ...(raw.internal_members || raw.internalMembers || []),
            ...(raw.clients || raw.Clients || []),
            ...(raw.agents || raw.Agents || []),
            ...(raw.reviewers || raw.Reviewers || [])
          ]
        }

        const getRole = (m) => (m.Role || m.role || '').toLowerCase()

        this.clientMembers = this.allMembersFlat.filter(m => ['client', 'dealer', 'factory'].includes(getRole(m)))
        this.agentMembers = this.allMembersFlat.filter(m => ['agent'].includes(getRole(m)))
        this.qcMembers = this.allMembersFlat.filter(m => ['reviewer', 'qc'].includes(getRole(m)))
        this.teamMembers = this.allMembersFlat.filter(m => {
          const role = getRole(m)
          return !['client', 'dealer', 'factory', 'agent'].includes(role)
        })
      } catch (e) {
        console.error('加载成员列表失败', e)
        this.$message.error('加载成员列表失败')
      } finally {
        this.loading = false
      }
    },

    openPermDialog(user, role) {
      this.permUser = user
      this.permUserRole = role || ''
      this.permDialogVisible = true
    }
  }
}
</script>

<style lang="scss" scoped>
.settings-page {
  max-width: 1100px;
}
</style>

<!-- 非 scoped 样式：提供子组件所需的公共样式 -->
<style lang="scss">
.settings-page {

  /* ====== 卡片 ====== */
  .settings-card {
    background: #fff;
    border-radius: 12px;
    padding: 24px 28px;
    margin-bottom: 20px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .card-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;

    &.blue { color: #1476ff; }
    &.danger { color: #f56c6c; }
  }

  .card-desc {
    margin: 4px 0 0;
    font-size: 13px;
    color: #94a3b8;
  }

  /* ====== 表格 ====== */
  .member-table-wrap { overflow-x: auto; }

  .member-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
      text-align: left;
      padding: 12px 16px;
      color: #94a3b8;
      font-weight: 500;
      font-size: 12px;
      border-top: 1px solid #f1f5f9;
      border-bottom: 1px solid #f1f5f9;
      white-space: nowrap;
      background: #fafbfc;
    }

    td {
      padding: 14px 16px;
      border-bottom: 1px solid #f1f5f9;
      color: #334155;
      vertical-align: middle;
    }

    tbody tr:hover td { background: #f8fafc; }
  }

  .td-muted { color: #94a3b8 !important; }

  /* ====== 成员头像 ====== */
  .member-name {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .member-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
  }

  /* ====== 角色标签 ====== */
  .role-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 500;
  }

  /* ====== 操作图标 ====== */
  .action-icon {
    cursor: pointer;
    font-size: 16px;
    color: #94a3b8;
    transition: color 0.2s;
    margin: 0 4px;

    &:hover { color: #1476ff; }
    &.danger:hover { color: #f56c6c; }
  }

  /* ====== 分页 ====== */
  .member-pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }

  /* ====== 按钮统一胶囊形样式 ====== */
  .el-button {
    border-radius: 20px !important;
    font-weight: 600 !important;
    padding: 8px 20px !important;
    font-size: 13px !important;
  }

  .el-button--primary {
    background-color: #1476ff !important;
    border-color: #1476ff !important;
    &:hover, &:focus { background-color: #0d5fd9 !important; border-color: #0d5fd9 !important; }
  }

  .el-button--warning {
    background-color: #e6a23c !important;
    border-color: #e6a23c !important;
    color: #fff !important;
    &:hover, &:focus { background-color: #cf8e2e !important; border-color: #cf8e2e !important; }
  }

  .el-button--success {
    background-color: #67c23a !important;
    border-color: #67c23a !important;
    color: #fff !important;
    &:hover, &:focus { background-color: #57a82d !important; border-color: #57a82d !important; }
  }

  .el-button--danger {
    background-color: #f56c6c !important;
    border-color: #f56c6c !important;
    color: #fff !important;
    &:hover, &:focus { background-color: #e04848 !important; border-color: #e04848 !important; }
  }

  .el-button--text {
    background: transparent !important;
    border: none !important;
    padding: 4px 8px !important;
  }

  .add-btn {
    font-weight: 600 !important;
  }

  /* ====== 表单 ====== */
  .form-row {
    display: flex;
    gap: 20px;
  }

  .form-half {
    flex: 1;
    min-width: 0;
  }

  .date-range {
    display: flex;
    align-items: center;
    gap: 8px;

    .el-date-editor { flex: 1; }
  }

  .date-sep { color: #94a3b8; }

  .form-actions {
    display: flex;
    justify-content: flex-end;
  }

  .save-btn {
    padding: 10px 32px !important;
  }

  /* ====== 危险区域 ====== */
  .danger-card {
    border: 1px solid #fee2e2;
    background: #fff8f6;
  }

  .danger-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 16px;
  }

  .danger-icon {
    font-size: 22px;
    color: #f56c6c;
    margin-top: 2px;
  }

  .danger-desc {
    margin: 4px 0 0;
    font-size: 13px;
    color: #94a3b8;
  }

  .delete-btn {
    font-weight: 600 !important;
  }
}

/* ====== 弹窗样式（需要全局，因为 append-to-body） ====== */
.add-member-dialog.el-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.dlg-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 28px 16px;
}

.dlg-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
}

.dlg-close {
  cursor: pointer;
  font-size: 18px;
  color: #c0c4cc;
  &:hover { color: #909399; }
}

.dlg-body {
  padding: 0 28px 20px;
}

.dlg-section {
  margin-bottom: 16px;
}

.dlg-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.dlg-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

.dlg-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px 28px 24px;
  border-top: 1px solid #f0f0f0;
}

.dlg-cancel-btn {
  border-radius: 20px !important;
  min-width: 80px !important;
  padding: 8px 20px !important;
}

.dlg-confirm-btn {
  border-radius: 20px !important;
  min-width: 80px !important;
  font-weight: 600 !important;
  padding: 8px 20px !important;
  background-color: #1476ff !important;
  border-color: #1476ff !important;
  &:hover, &:focus { background-color: #0d5fd9 !important; border-color: #0d5fd9 !important; }
}

/* ====== 角色选择药丸 ====== */
.role-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.role-pill {
  display: inline-block;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ed;
  color: #606266;
  background: #fff;
  transition: all 0.2s;

  &:hover { border-color: #1476ff; color: #1476ff; }
  &.active {
    background: #1476ff;
    border-color: #1476ff;
    color: #fff;
  }
}

/* ====== 角色信息提示 ====== */
.role-info-box {
  background: #f0f7ff;
  border-radius: 10px;
  padding: 14px 18px;
  margin-top: 12px;
}

.role-info-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.role-info-icon {
  color: #1476ff;
  font-size: 15px;
}

.role-info-desc {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

/* ====== 确认弹窗风格 ====== */
.settings-confirm-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
}

/* 权限弹窗 */
.perm-dialog.el-dialog {
  border-radius: 16px !important;
  overflow: hidden !important;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}
</style>
